import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { useAddBrand } from "../hooks/useAddBrand";
import { useExtractBrandInfo, type ExtractBrandInfoResult } from "../hooks/useExtractBrandInfo";
import { useOrganization } from "@/domains/organizations";

import {
  type AddBrandModalProps,
  AddBrandModalHeader,
  AddBrandStepFind,
  AddBrandStepReview,
  AddBrandStepIndustry,
  AddBrandModalFooter,
} from "@/components/brands/form";

export type { AddBrandModalProps };

export const AddBrandModal: React.FC<AddBrandModalProps> = ({
  open,
  setOpen,
  existingBrands = [],
  onSuccess,
}) => {
  const { currentOrganization } = useOrganization();

  // Step 1: Website
  const [fetchUrl, setFetchUrl] = useState("");

  // Step 2 & 3: Extracted Brand Details
  const [brandName, setBrandName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const [industry, setIndustry] = useState("");

  const addBrandMutation = useAddBrand();
  const extractMutation = useExtractBrandInfo();

  // Minimal duplicate prevention using name
  const isDuplicate = existingBrands.some(
    (b) => b.name?.trim().toLowerCase() === brandName.trim().toLowerCase()
  );

  const extraction = extractMutation.data;
  const existingBrandMatch = extraction?.status === "duplicate" ? extraction : null;
  const isDetailsFetched = Boolean(extraction?.status === "ok" && brandName.trim());
  const searchCandidates = extraction?.status === "candidates" ? extraction.results : null;

  const resetState = useCallback(() => {
    setFetchUrl("");
    setBrandName("");
    setLogoUrl("");
    setWebsite("");
    setDescription("");
    setIndustry("");
    extractMutation.reset();
  }, [extractMutation]);

  const handleClose = useCallback(() => {
    resetState();
    setOpen(false);
  }, [resetState, setOpen]);

  const handleFetchUrlChange = (value: string) => {
    setFetchUrl(value);
    if (extractMutation.data || extractMutation.error) {
      extractMutation.reset();
    }
  };

  const applyExtraction = (result: ExtractBrandInfoResult) => {
    if (result.status !== "ok") return;

    setBrandName(result.data.brandName || "");
    setWebsite(result.data.websiteUrl);
    setLogoUrl(result.data.logoUrl ?? "");
    if (result.data.description) {
      setDescription(result.data.description);
    }
  };

  const handleFetchBrandDetails = () => {
    if (!fetchUrl.trim() || extractMutation.isPending) return;
    extractMutation.mutate(fetchUrl.trim(), { onSuccess: applyExtraction });
  };

  const handleSelectCandidate = (domain: string) => {
    if (extractMutation.isPending) return;
    extractMutation.mutate(`https://${domain}`, { onSuccess: applyExtraction });
  };

  const handleUseExistingBrand = () => {
    if (!existingBrandMatch) return;

    onSuccess?.({
      id: existingBrandMatch.brandId,
      name: existingBrandMatch.brandName,
      logo_url: null,
    });
    handleClose();
  };

  const handleAddBrand = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrganization?.id) {
      toast({
        title: "No organization selected",
        description: "Please select an organization first.",
        variant: "destructive",
      });
      return;
    }

    if (!brandName || !industry) {
      toast({
        title: "Required fields missing",
        description: "Please ensure the brand name and industry are specified.",
        variant: "destructive",
      });
      return;
    }

    if (isDuplicate) {
      toast({
        title: "Duplicate Brand",
        description: "A brand with the same name already exists.",
        variant: "destructive",
      });
      return;
    }

    if (existingBrandMatch) {
      toast({
        title: "Brand already exists",
        description: `${existingBrandMatch.brandName} is already in your portfolio.`,
        variant: "destructive",
      });
      return;
    }

    const brandData = {
      name: brandName.trim(),
      description: description.trim() || undefined,
      industry: industry.trim() || undefined,
      website_url: website.trim() || fetchUrl.trim() || undefined,
      logo_url: logoUrl || undefined,
    };

    addBrandMutation.mutate(brandData, {
      onSuccess: (newBrand) => {
        handleClose();
        if (onSuccess) {
          onSuccess(newBrand);
        }
      },
    });
  };

  if (!currentOrganization) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="w-[90vw] max-w-lg p-6 text-center rounded-2xl">
          <DialogTitle className="text-lg font-semibold text-slate-900">Add New Brand</DialogTitle>
          <p className="text-sm text-slate-600 mt-2">Please select an organization first.</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-[620px] p-0 rounded-2xl overflow-hidden border-none shadow-2xl bg-white focus:outline-none [&>button:last-child]:hidden">
        <form onSubmit={handleAddBrand} className="flex flex-col">
          {/* Header */}
          <AddBrandModalHeader onClose={handleClose} />

          {/* Stepper Content */}
          <div className="p-6 space-y-6">
            {/* Step 1: Find Brand */}
            <AddBrandStepFind
              fetchUrl={fetchUrl}
              onFetchUrlChange={handleFetchUrlChange}
              onFetchBrandDetails={handleFetchBrandDetails}
              isPending={extractMutation.isPending}
              isDetailsFetched={isDetailsFetched}
              searchCandidates={searchCandidates}
              onSelectCandidate={handleSelectCandidate}
              existingBrandMatch={existingBrandMatch}
              onUseExistingBrand={handleUseExistingBrand}
              hasOnSuccess={Boolean(onSuccess)}
            />

            {/* Step 2: Review Details */}
            <AddBrandStepReview
              isDetailsFetched={isDetailsFetched}
              brandName={brandName}
              onBrandNameChange={setBrandName}
              logoUrl={logoUrl}
              onLogoError={() => setLogoUrl("")}
            />

            {/* Step 3: Select Industry */}
            <AddBrandStepIndustry
              isDetailsFetched={isDetailsFetched}
              industry={industry}
              onIndustryChange={setIndustry}
            />
          </div>

          {/* Footer */}
          <AddBrandModalFooter
            onClose={handleClose}
            isSubmitDisabled={
              !isDetailsFetched ||
              !brandName.trim() ||
              !industry ||
              addBrandMutation.isPending ||
              Boolean(existingBrandMatch) ||
              isDuplicate
            }
            isPending={addBrandMutation.isPending}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddBrandModal;
